import type { WebStorage } from "redux-persist";
import persistStorage from "redux-persist/lib/storage";

import { getTabSessionId } from "@repo/ui/lib/tabSessionId";

const isServer = typeof globalThis.window === "undefined";
const isTestEnv = process.env.NODE_ENV === "test";

/**
 * Get the tab-specific storage key.
 * Since ReduxInitializer guarantees session ID exists before Redux mounts,
 * we can safely use getTabSessionId() synchronously without waiting.
 */
function getTabSpecificKey(key: string): string | null {
  if (isServer || isTestEnv) {
    return key;
  }

  try {
    const tabSessionId = getTabSessionId();

    if (!tabSessionId) {
      // Return null to prevent fallback to generic 'persist:redux' key
      // which may contain old session data
      return null;
    }

    return `${key}:${tabSessionId}`;
  } catch {
    return null;
  }
}

/**
 * Tab-aware storage for redux-persist.
 * Uses session ID to namespace localStorage keys per tab.
 *
 * IMPORTANT: This assumes ReduxInitializer has already ensured session ID exists.
 * If used without ReduxInitializer, session ID may not be available.
 */
const tabAwareStorage: WebStorage = isServer
  ? {
      getItem: async () => null,
      setItem: async () => {},
      removeItem: async () => {},
    }
  : {
      getItem: async (key: string): Promise<string | null> => {
        const tabKey = getTabSpecificKey(key);

        // If no session ID, return null to prevent reading stale data
        if (!tabKey) {
          return null;
        }

        return persistStorage.getItem(tabKey);
      },
      setItem: async (key: string, value: string): Promise<void> => {
        const tabKey = getTabSpecificKey(key);

        // If no session ID, don't write to prevent writing to wrong key
        if (!tabKey) {
          return;
        }

        return persistStorage.setItem(tabKey, value);
      },
      removeItem: async (key: string): Promise<void> => {
        const tabKey = getTabSpecificKey(key);

        // If no session ID, can't remove correctly
        if (!tabKey) {
          return;
        }

        return persistStorage.removeItem(tabKey);
      },
    };

export default tabAwareStorage;
